import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TZS = ["Europe/London", "Asia/Kolkata", "Asia/Dubai", "America/New_York", "Australia/Sydney"];
const L = ({ label, children }) => <label className="block text-xs font-semibold text-slate-600">{label}<div className="mt-1 font-normal">{children}</div></label>;

const GoogleCard = ({ g, headers, reload }) => {
  const connect = async () => {
    try { window.location.href = (await axios.get(`${API}/admin/google-calendar/connect`, { headers })).data.authorization_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't start Google sign-in"); }
  };
  const disconnect = async () => { await axios.delete(`${API}/admin/google-calendar`, { headers }); reload(); };
  return (
    <div className="rounded-xl border bg-white p-4 flex flex-wrap items-center gap-3" data-testid="gcal-card">
      <CalendarDays className="w-8 h-8 text-[#2563eb]" />
      <div className="flex-1 min-w-[200px] text-sm">
        <p className="font-semibold">Google Calendar sync</p>
        <p className="text-slate-500" data-testid="gcal-status">
          {!g?.configured ? "Waiting for Google Calendar credentials (GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET)."
            : g.connected ? `Connected as ${g.email}. Bookings appear in your calendar and busy times are hidden.` : "Not connected yet."}
        </p>
      </div>
      {g?.configured && (g.connected
        ? <Button variant="outline" size="sm" onClick={disconnect} data-testid="gcal-disconnect">Disconnect</Button>
        : <Button size="sm" onClick={connect} data-testid="gcal-connect">Connect Google Calendar</Button>)}
    </div>
  );
};

export const BookingSettings = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState("");
  const load = () => axios.get(`${API}/admin/trial/settings`, { headers }).then((r) => setS(r.data));
  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search).get("gcal");
    if (q) toast[q === "connected" ? "success" : "error"](q === "connected" ? "Google Calendar connected" : "Google Calendar connection failed");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return <Loader2 className="w-5 h-5 animate-spin" />;
  const setDay = (d, wins) => setS({ ...s, weekly: { ...s.weekly, [d]: wins } });
  const save = async () => {
    setBusy(true);
    const { google, key, ...body } = s;
    try { setS({ ...(await axios.put(`${API}/admin/trial/settings`, body, { headers })).data, google }); toast.success("Booking settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const num = (k) => <Input type="number" value={s[k]} onChange={(e) => setS({ ...s, [k]: Number(e.target.value) })} data-testid={`booking-${k}`} />;
  return (
    <div className="space-y-4" data-testid="booking-settings">
      <GoogleCard g={s.google} headers={headers} reload={load} />
      <div className="rounded-xl border bg-white p-4 grid sm:grid-cols-3 gap-3">
        <L label="Your time zone"><select className="h-9 w-full rounded-md border px-2 text-sm" value={s.timezone} onChange={(e) => setS({ ...s, timezone: e.target.value })} data-testid="booking-timezone">{[...new Set([s.timezone, ...TZS])].map((t) => <option key={t}>{t}</option>)}</select></L>
        <L label="Session length (min)">{num("slot_minutes")}</L>
        <L label="Gap between sessions (min)">{num("buffer_minutes")}</L>
        <L label="Minimum notice (hours)">{num("min_notice_hours")}</L>
        <L label="Book up to (days ahead)">{num("max_days_ahead")}</L>
        <L label="Send booking alerts to"><Input value={s.admin_email} onChange={(e) => setS({ ...s, admin_email: e.target.value })} data-testid="booking-admin-email" /></L>
        <div className="sm:col-span-2"><L label="Fixed meeting link (Zoom / Meet) - optional"><Input value={s.meeting_link} placeholder="https://" onChange={(e) => setS({ ...s, meeting_link: e.target.value })} data-testid="booking-meeting-link" /></L></div>
        <label className="flex items-center gap-2 text-sm mt-5"><input type="checkbox" checked={!!s.use_google_meet} onChange={(e) => setS({ ...s, use_google_meet: e.target.checked })} data-testid="booking-use-meet" />Auto-create Google Meet link</label>
      </div>
      <div className="rounded-xl border bg-white p-4 space-y-2">
        <h4 className="font-semibold">Weekly availability ({s.timezone})</h4>
        {DAYS.map((name, d) => (
          <div key={d} className="flex flex-wrap items-center gap-2 border-b last:border-0 py-2" data-testid={`booking-day-${d}`}>
            <span className="w-24 text-sm font-medium">{name}</span>
            {(s.weekly[d] || []).map((w, i) => (
              <span key={i} className="flex items-center gap-1">
                <input type="time" className="h-8 rounded border px-1 text-sm" value={w.start} onChange={(e) => setDay(d, s.weekly[d].map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} data-testid={`booking-day-${d}-${i}-start`} />–
                <input type="time" className="h-8 rounded border px-1 text-sm" value={w.end} onChange={(e) => setDay(d, s.weekly[d].map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} data-testid={`booking-day-${d}-${i}-end`} />
                <button onClick={() => setDay(d, s.weekly[d].filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </span>
            ))}
            {!(s.weekly[d] || []).length && <span className="text-xs text-slate-400">Unavailable</span>}
            <Button size="sm" variant="ghost" onClick={() => setDay(d, [...(s.weekly[d] || []), { start: "16:00", end: "18:00" }])} data-testid={`booking-day-${d}-add`}><Plus className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
      </div>
      <div className="rounded-xl border bg-white p-4 space-y-2">
        <h4 className="font-semibold">Blocked dates (holidays)</h4>
        <div className="flex gap-2"><Input type="date" value={blocked} onChange={(e) => setBlocked(e.target.value)} className="w-48" data-testid="booking-block-date" />
          <Button size="sm" variant="outline" disabled={!blocked} onClick={() => { setS({ ...s, blocked_dates: [...new Set([...s.blocked_dates, blocked])].sort() }); setBlocked(""); }} data-testid="booking-block-add">Block</Button></div>
        <div className="flex flex-wrap gap-2">{s.blocked_dates.map((d) => <span key={d} className="text-xs bg-slate-100 rounded-full px-2 py-1">{d} <button onClick={() => setS({ ...s, blocked_dates: s.blocked_dates.filter((x) => x !== d) })}>×</button></span>)}</div>
      </div>
      <div className="rounded-xl border bg-white p-4 space-y-2">
        <h4 className="font-semibold">Programmes shown in the form</h4>
        {s.programmes.map((p, i) => (
          <div key={i} className="flex gap-2"><Input value={p} onChange={(e) => setS({ ...s, programmes: s.programmes.map((x, j) => (j === i ? e.target.value : x)) })} data-testid={`booking-programme-${i}`} />
            <Button size="icon" variant="ghost" onClick={() => setS({ ...s, programmes: s.programmes.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></Button></div>
        ))}
        <Button size="sm" variant="outline" onClick={() => setS({ ...s, programmes: [...s.programmes, ""] })} data-testid="booking-programme-add"><Plus className="w-3.5 h-3.5 mr-1" />Add programme</Button>
      </div>
      <Button onClick={save} disabled={busy} data-testid="booking-settings-save">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save booking settings</Button>
    </div>
  );
};
